import { useApi } from '@/@diffrag/hooks/useApi'
import { ContentDetailView } from '@/@diffrag/layout/ContentDetailView'
import { api } from '@/api'
import { useQueryValue } from '@/@diffrag/hooks/useQueryValue'
import { ContentView } from '@/@diffrag/layout/ContentView'
import { OffsetPagination } from '@/@diffrag/OffsetPagination'
import { SimpleTable } from '@/@diffrag/table/SimpleTable'
import { useMemo, useState } from 'react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ChevronDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useNavigate } from 'react-router-dom'
import { IAdminCommunityRO, IAdminExperienceRO } from 'api'

export default function Experience() {
  const q = useQueryValue('q', undefined)
  const page = useQueryValue('page', 1)
  const regionId = useQueryValue('regionId', undefined)
  const categoryId = useQueryValue('categoryId', undefined)
  const navigate = useNavigate()

  const [selectedCategory, setSelectedCategory] = useState<number | undefined>(
    undefined,
  )
  const [selectedRegion, setSelectedRegion] = useState<number | undefined>(
    undefined,
  )

  const { data: regionData } = useApi({
    id: 'regionList',
    fn: api().adminRegion.adminRegionList,
  })

  const { data: categories } = useApi({
    id: 'categoryList',
    fn: api().adminCategory.adminCategoryList,
  })

  const { data, refetch } = useApi({
    id: 'experienceList',
    fn: api().adminExperience.getExperienceList,
    params: {
      q,
      page,
      pageSize: 20,
      regionId: selectedRegion,
      categoryId: selectedCategory,
    },
  })

  const selectedRegionLabels = useMemo(() => {
    if (!selectedRegion) {
      return '전체'
    }
    return (
      regionData?.items
        ?.filter((item) => selectedRegion === item.id)
        .map((item) => item.name)
        .join(', ') || '전체'
    )
  }, [selectedRegion, regionData])

  const selectedCategoryLabels = useMemo(() => {
    if (!selectedCategory) {
      return '전체'
    }
    return (
      categories?.items
        ?.filter((item) => selectedCategory === item.id)
        .map((item) => item.name)
        .join(', ') || '전체'
    )
  }, [selectedCategory, categories])

  const onDelete = async (rows: IAdminExperienceRO[]) => {
    try {
      await Promise.all(
        rows.map((row) => api().adminCommunity.deleteCommunityArticle(row.id)),
      )

      refetch()
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <ContentView>
      <div className="mb-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">카테고리 필터:</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="min-w-[150px] justify-between"
              >
                {selectedCategoryLabels}
                <ChevronDown className="ml-2 size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="z-50 w-[150px] border bg-white shadow-lg"
            >
              <DropdownMenuCheckboxItem
                checked={selectedCategory === undefined}
                onCheckedChange={() => setSelectedCategory(undefined)}
              >
                전체
              </DropdownMenuCheckboxItem>
              {categories?.items?.map((category) => (
                <DropdownMenuCheckboxItem
                  key={category.id}
                  checked={selectedCategory === category.id}
                  onCheckedChange={() => setSelectedCategory(category.id)}
                >
                  {category.name}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">지역 필터:</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="min-w-[150px] justify-between"
              >
                {selectedRegionLabels}
                <ChevronDown className="ml-2 size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="z-50 w-[150px] border bg-white shadow-lg"
            >
              <DropdownMenuCheckboxItem
                checked={selectedRegion === undefined}
                onCheckedChange={() => setSelectedRegion(undefined)}
              >
                전체
              </DropdownMenuCheckboxItem>
              {regionData?.items?.map((region) => (
                <DropdownMenuCheckboxItem
                  key={region.id}
                  checked={selectedRegion === region.id}
                  onCheckedChange={() => setSelectedRegion(region.id)}
                >
                  {region.name}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <SimpleTable
        options={{
          disableNew: true,
          disableSort: true,
        }}
        onDelete={onDelete}
        data={data?.rows}
        onRowClick={(row) => {
          navigate(`/experience/edit?id=${row.id}`)
        }}
        columns={[
          {
            label: 'NO',
            key: 'id',
            isPrimary: true,
          },
          {
            label: '카테고리',
            key: 'category.name',
            isPrimary: true,
          },
          {
            label: '지역',
            key: 'region.name',
            isPrimary: true,
          },
          {
            key: 'author',
            label: '작성자',
            isPrimary: true,
            formatter: (value) => {
              return value?.clinic?.name || value?.userData?.nickname || '-'
            },
          },
          {
            label: '체험단 제목',
            key: 'title',
            isPrimary: true,
          },
        ]}
      />
      <OffsetPagination page={page} total={data?.count} pageSize={20} />
    </ContentView>
  )
}
